import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { AiCosts, aiCostsApi } from '../api/aiCosts';
import { customerApi } from '../api/machines';
import { Customer, Integrator } from '../types';
import IntegratorAutocomplete from '../components/IntegratorAutocomplete';
import { useAuth } from '../context/AuthContext';

const FEATURE_LABELS: Record<string, string> = {
  RAG_REPLY: 'Auto-odpowiedź (klient)',
  TICKET_ANALYSIS: 'Analiza dla serwisanta',
  INDEXING: 'Indeksowanie dokumentacji',
  NOTES: 'Notatki (embeddingi/wyszukiwanie)',
  OTHER: 'Inne',
};

const isoDay = (d: Date) => d.toISOString().slice(0, 10);
const fmtUsd = (v: number, digits = 4) => `$${Number(v).toFixed(digits)}`;
const fmtInt = (v: number) => Number(v).toLocaleString('pl-PL');

const Card: React.FC<{ label: string; value: string; sub?: string; color?: string }> = ({ label, value, sub, color }) => (
  <div className="card" style={{ padding: '1.25rem', borderTop: `4px solid ${color || 'var(--color-primary)'}`, minWidth: 0 }}>
    <div className="eyebrow" style={{ marginBottom: '0.5rem', color: 'var(--color-text-muted)' }}>{label}</div>
    <div style={{ fontSize: '2rem', fontWeight: 700, letterSpacing: '-0.02em' }}>{value}</div>
    {sub && <div className="item-meta" style={{ marginTop: '0.25rem' }}>{sub}</div>}
  </div>
);

const cellStyle: React.CSSProperties = {
  padding: '0.5rem 0.75rem',
  borderBottom: '1px solid var(--color-border)',
  fontSize: '0.875rem',
};
const headStyle: React.CSSProperties = {
  ...cellStyle,
  fontWeight: 600,
  color: 'var(--color-text-muted)',
  textAlign: 'left',
  whiteSpace: 'nowrap',
};
const numStyle: React.CSSProperties = { ...cellStyle, textAlign: 'right', fontVariantNumeric: 'tabular-nums' };

const CostTable: React.FC<{
  title: string;
  keyHeader: string;
  rows: { key: string; costUsd: number; calls: number; tokens: number }[];
  renderKey?: (key: string) => string;
}> = ({ title, keyHeader, rows, renderKey }) => (
  <div className="card" style={{ padding: '1rem 1.25rem', minWidth: 0 }}>
    <div className="eyebrow" style={{ marginBottom: '0.75rem', color: 'var(--color-text-muted)' }}>{title}</div>
    {rows.length === 0 ? (
      <div className="item-meta">Brak danych w wybranym zakresie.</div>
    ) : (
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={headStyle}>{keyHeader}</th>
              <th style={{ ...headStyle, textAlign: 'right' }}>Koszt</th>
              <th style={{ ...headStyle, textAlign: 'right' }}>Wywołania</th>
              <th style={{ ...headStyle, textAlign: 'right' }}>Tokeny</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.key}>
                <td style={cellStyle}>{renderKey ? renderKey(r.key) : r.key}</td>
                <td style={numStyle}>{fmtUsd(r.costUsd)}</td>
                <td style={numStyle}>{fmtInt(r.calls)}</td>
                <td style={numStyle}>{fmtInt(r.tokens)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}
  </div>
);

const AiCostsPage: React.FC = () => {
  const { user } = useAuth();
  const isSuperAdmin = !!user?.roles?.includes('ADMIN');

  const today = new Date();
  const monthAgo = new Date(today.getTime() - 29 * 86_400_000);

  const [firm, setFirm] = useState<Integrator | null>(null);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [from, setFrom] = useState(isoDay(monthAgo));
  const [to, setTo] = useState(isoDay(today));
  const [data, setData] = useState<AiCosts | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    customerApi.getAll()
      .then(({ data }) => setCustomers(data))
      .catch(() => { /* autocomplete po prostu nie podpowie firm */ });
  }, []);

  // Klienci (firmy z NIP-em) w kształcie akceptowanym przez wspólny autocomplete firm.
  const firmOptions: Integrator[] = useMemo(() => customers.map((c) => ({
    id: c.id,
    name: c.companyName || `NIP ${c.nip}`,
    nip: c.nip,
    billingStreet: null, billingBuildingNo: null, billingApartmentNo: null,
    billingPostCode: null, billingCity: null, billingEmail: null,
    logoUrl: null, createdAt: null,
  })), [customers]);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await aiCostsApi.get({
        nip: firm?.nip || undefined,
        from: from || undefined,
        to: to || undefined,
      });
      setData(data);
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.response?.data?.message || 'Nie udało się pobrać kosztów AI');
    } finally {
      setLoading(false);
    }
  }, [firm, from, to]);

  useEffect(() => { load(); }, [load]);

  if (!isSuperAdmin) {
    return <Navigate to="/machines" replace />;
  }

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Koszty AI</h1>
      </div>

      <div className="filters-bar">
        <div className="filter-group">
          <IntegratorAutocomplete
            integrators={firmOptions}
            value={firm}
            onChange={setFirm}
            label="Firma (wpisz NIP lub nazwę)"
          />
        </div>
        <div className="filter-group">
          <label className="form-label">Od</label>
          <input className="form-input" type="date" value={from} max={to} onChange={(e) => setFrom(e.target.value)} />
        </div>
        <div className="filter-group">
          <label className="form-label">Do</label>
          <input className="form-input" type="date" value={to} min={from} onChange={(e) => setTo(e.target.value)} />
        </div>
      </div>

      {error && <div className="alert alert-error">{error}</div>}
      {loading && !data && <div className="item-meta">Ładowanie…</div>}

      {data && (
        <>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '1rem',
            marginBottom: '1.5rem',
          }}>
            <Card label="Koszt łącznie" value={fmtUsd(data.totalCostUsd, 2)} sub="wg cen z konfiguracji" />
            <Card label="Wywołania API" value={fmtInt(data.totalCalls)} color="#1e78d6" />
            <Card label="Tokeny wejściowe" value={fmtInt(data.totalPromptTokens)} color="#949ba4" />
            <Card label="Tokeny wyjściowe" value={fmtInt(data.totalCompletionTokens)} color="#2e9956" />
          </div>

          <div style={{ display: 'grid', gap: '1.5rem' }}>
            <CostTable
              title="Koszty per dzień"
              keyHeader="Dzień"
              rows={data.byDay.map((d) => ({ key: d.day, costUsd: d.costUsd, calls: d.calls, tokens: d.tokens }))}
            />
            <CostTable
              title="Koszty per funkcja"
              keyHeader="Funkcja"
              rows={data.byFeature}
              renderKey={(k) => FEATURE_LABELS[k] || k}
            />
            <CostTable
              title="Koszty per klient (NIP)"
              keyHeader="NIP"
              rows={data.byNip}
            />
          </div>
        </>
      )}
    </div>
  );
};

export default AiCostsPage;
